
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Network, CheckCircle2, XCircle, Workflow } from 'lucide-react';
import { Reveal } from './Reveal';

export const TechComparison: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section id="solutions" className="py-20 md:py-40 bg-rabbit-navy text-white border-y border-gray-800 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-900/10 blur-[120px] rounded-full pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">

        <Reveal>
          <div className="text-center mb-16 md:mb-24">
            <div className="inline-block px-4 py-2 bg-clever-teal/10 text-clever-teal text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-clever-teal/20">
              {t('shared.tech_comparison.tagline')}
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-8 text-white tracking-tight leading-tight uppercase">
              {t('shared.tech_comparison.title_line1')} <br />
              <span className="text-gray-500">{t('shared.tech_comparison.title_line2')}</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg leading-relaxed">
              {t('shared.tech_comparison.subtitle')}
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-stretch">

          {/* Left: Silos (Problem) */}
          <Reveal delay={0.1}>
            <div className="bg-white/5 h-full rounded-none p-8 md:p-12 border border-white/10 flex flex-col backdrop-blur-sm hover:bg-white/10 transition-colors group">
              <div className="flex items-center gap-4 mb-10 text-gray-500 group-hover:text-gray-400 transition-colors">
                <Network size={32} />
                <h3 className="font-bold text-2xl text-gray-300">{t('shared.tech_comparison.problem.title')}</h3>
              </div>

              <div className="flex-1">
                <p className="text-gray-400 mb-10 leading-relaxed text-lg">
                  {t('shared.tech_comparison.problem.desc')}
                </p>

                <ul className="space-y-4 mb-8 opacity-60">
                  <li className="flex items-center gap-3 text-sm text-red-400 font-medium">
                    <XCircle size={18} /> <span>{(t('shared.tech_comparison.problem.bullets', { returnObjects: true }) as string[])[0]}</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-red-400 font-medium">
                    <XCircle size={18} /> <span>{(t('shared.tech_comparison.problem.bullets', { returnObjects: true }) as string[])[1]}</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-red-400 font-medium">
                    <XCircle size={18} /> <span>{(t('shared.tech_comparison.problem.bullets', { returnObjects: true }) as string[])[2]}</span>
                  </li>
                </ul>

                <div className="mt-auto flex gap-2 flex-wrap opacity-40 grayscale">
                  <span className="px-3 py-1 border border-white/30 rounded text-xs">CRM</span>
                  <span className="px-3 py-1 border border-white/30 rounded text-xs">ERP</span>
                  <span className="px-3 py-1 border border-white/30 rounded text-xs">Mail</span>
                  <span className="px-3 py-1 border border-white/30 rounded text-xs">Excel</span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right: Orchestration (Solution) */}
          <Reveal delay={0.2}>
            <div className="relative h-full group">
              <div className="absolute inset-0 bg-clever-teal blur-[100px] opacity-10 group-hover:opacity-20 transition-opacity duration-1000 pointer-events-none"></div>

              <div className="relative h-full bg-[#050505] p-8 md:p-12 shadow-2xl flex flex-col overflow-hidden border border-gray-800">

                <div className="absolute top-0 right-0 bg-clever-teal text-white text-[10px] font-bold px-6 py-2 uppercase tracking-widest">
                  {t('shared.tech_comparison.solution.tag')}
                </div>

                <div className="flex items-center gap-4 mb-10 text-white">
                  <Workflow size={32} className="text-clever-teal" />
                  <h3 className="font-bold text-2xl text-white">{t('shared.tech_comparison.solution.title')}</h3>
                </div>

                <p className="text-gray-400 mb-10 leading-relaxed text-lg">
                  {t('shared.tech_comparison.solution.desc')}
                </p>

                {/* Visual: Workflow Logic */}
                <div className="bg-[#0a0a0a] p-6 font-mono text-xs overflow-hidden border-l-2 border-clever-teal shadow-inner relative mb-10 group-hover:border-l-4 transition-all">
                  <div className="text-gray-500 leading-relaxed">
                    <span className="text-purple-400">workflow</span> <span className="text-yellow-200">HandleIncomingOrder</span>:<br />
                    &nbsp;&nbsp;<span className="text-blue-400">Trigger</span>: Email_Received<br />
                    &nbsp;&nbsp;<span className="text-blue-400">Step 1</span>: AI.Analyze(Content, Attachments)<br />
                    &nbsp;&nbsp;<span className="text-blue-400">Step 2</span>: CRM.Update(Customer_ID)<br />
                    &nbsp;&nbsp;<span className="text-blue-400">Step 3</span>: ERP.CreateOrder(Items)<br />
                    &nbsp;&nbsp;<span className="text-green-400">Result</span>: "{t('shared.tech_comparison.solution.workflow.result')}"
                  </div>
                </div>

                <ul className="space-y-4 mt-auto">
                  <li className="flex items-center gap-3 text-sm text-white font-bold uppercase tracking-wider">
                    <CheckCircle2 size={18} className="text-clever-teal" />
                    <span>{(t('shared.tech_comparison.solution.bullets', { returnObjects: true }) as string[])[0]}</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-white font-bold uppercase tracking-wider">
                    <CheckCircle2 size={18} className="text-clever-teal" />
                    <span>{(t('shared.tech_comparison.solution.bullets', { returnObjects: true }) as string[])[1]}</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-white font-bold uppercase tracking-wider">
                    <CheckCircle2 size={18} className="text-clever-teal" />
                    <span>{(t('shared.tech_comparison.solution.bullets', { returnObjects: true }) as string[])[2]}</span>
                  </li>
                </ul>
              </div>
            </div>
          </Reveal>

        </div>
      </div>
    </section>
  );
};
